import { useMemo, useState, type FC } from 'react';
import type { HeroDetailedProps } from './types';

import styled, { css } from 'styled-components';
import {
  ApiService,
  COLOR_ORANGE,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Button, Wrapper, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { HoverableDropzone } from '../HoverableDropzone';

import { shade } from 'shared/helpers/utils';

const HeroDetailed = styled.div`
  position: relative;
  overflow-x: hidden;
  min-height: 693px;
`;

const WrapperTitle = styled((props) => <Wrapper {...props} />)`
  padding: 96px 0 44px;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 710px) {
    padding: 80px 0 30px;
  }

  @media screen and (max-width: 480px) {
    padding: 80px 0 10px;
  }
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0 0;
  max-width: 100%;
  gap: 50px;
  margin-bottom: 80px;

  @media screen and (max-width: 991px) {
    padding: 10px 0 0;
    margin-bottom: 50px;
  }

  @media screen and (max-width: 906px) {
    gap: 40px;
    flex-direction: column-reverse;
    margin-bottom: 40px;
    padding: 10px 0 0;
  }

  @media screen and (max-width: 480px) {
    padding: 10px 0 0;
  }
`;

const TitleWrapper = styled.div`
  width: 655px;
  position: relative;
  z-index: 7;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}

    @media screen and (max-width: 480px) {
    margin-top: 0;
  }
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.25em;
  color: #fff;
  position: relative;
  z-index: 5;
  text-align: center;
  padding: 0 80px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  @media screen and (max-width: 710px) {
    padding: 0px;
  }
`;

const DescWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 5;
  padding: 20px 10% 140px;
  text-align: center;

  @media screen and (max-width: 991px) {
    padding-top: 0;
  }

  @media screen and (max-width: 575px) {
    padding: 10px 0 96px;
  }
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 50px;
  line-height: 1.35em;
  color: #fff;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 59px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 40px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ButtonInner = styled.div`
  position: relative;
  width: fit-content;
`;

const StyledButton = styled(Button)`
  max-width: 568px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    padding-top: 24px;
    padding-bottom: 26px;
  `}
`;

const RelativeWrapper = styled.div`
  position: relative;
  z-index: 6;
  width: 704px;
  height: 404px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: -4%;

  @media screen and (max-width: 991px) {
    right: -3%;
    height: 464px;
  }

  @media screen and (max-width: 710px) {
    left: 0;
    right: 0;
    height: 404px;
  }

  @media screen and (max-width: 480px) {
    width: 580px;
    height: 364px;
  }
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 506px;
  height: auto;
  object-fit: fill;
  z-index: 2;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 438px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 340px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 370 &&
    `
    width: 270px;
  `}
`;

const BenefitWrapper = styled.div`
  position: relative;
`;

const Benefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  width: 100%;
  position: relative;
  z-index: 24;

  @media screen and (max-width: 480px) {
    gap: 20px;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  gap: 10px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: row;
    width: 100%;
    border-radius: 12px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
    `}
`;

const IconBenefit = styled.div`
  width: 62px;
  height: 62px;
  margin-right: 18px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0px 2px 8.1px rgba(31, 31, 31, 0.12);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 30px;
    height: 30px;
  }

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 52px;
      height: 52px;
      margin-bottom: 8px;
    `}
`;

const BenefitTxtInner = styled.div`
  width: 78%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
      text-align: left;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 6px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const WhiteInner = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 3;
  overflow: hidden;
`;

const RelativeWhite = styled.div`
  position: relative;
  width: 1920px;
  height: 100%;
  margin: 0 auto;

  .hero-white-v5 {
    border-radius: 50%;
    filter: blur(128px);
    background: #fff;
    position: absolute;
    margin: 0 auto;
    left: 0;
    right: 0;
  }

  .hero-white-v5_one {
    width: 810px;
    height: 810px;
    opacity: 0.2;
    bottom: -20%;
    right: -44%;
  }

  .hero-white-v5_two {
    width: 522px;
    height: 522px;
    opacity: 0.06;
    background: #fff;
    right: -38%;
    bottom: 25%;
  }

  .hero-white-v5_three {
    width: 609px;
    height: 609px;
    opacity: 0.15;
    background: #fff;
    right: -28%;
    bottom: 30%;
  }

  .hero-white-v5_four {
    width: 1307px;
    height: 1307px;
    opacity: 0.2;
    background: #fff;
    left: -52%;
    bottom: -74%;
  }

  .hero-white-v5_five {
    width: 322px;
    height: 322px;
    opacity: 0.23;
    background: #fff;
    left: -78%;
    top: 1%;
  }

  .hero-white-v5_six {
    width: 322px;
    height: 322px;
    opacity: 0.24;
    background: #fff;
    right: -78%;
    top: 1%;
  }

  @media screen and (max-width: 575px) {
    .hero-white-v5_three {
      width: 609px;
      height: 609px;
      opacity: 0.35;
      background: #fff;
      right: 22%;
      bottom: auto;
      top: -3%;
    }

    .hero-white-v5_six {
      width: 322px;
      height: 322px;
      opacity: 0.54;
      background: #fff;
      left: 0;
      right: auto;
      top: 21%;
    }

    .hero-white-v5_two {
      width: 382px;
      height: 382px;
      opacity: 0.5;
      background: #fff;
      right: auto;
      bottom: 15%;
    }
  }
`;

const ColoredBg = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    height: 100%;
    `}
`;

const ImageRelative = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const AbsoluteInner = styled.div`
  position: absolute;
  top: 6.5%;
  left: 18.1%;
  z-index: 1;

  @media screen and (max-width: 710px) {
    top: 11%;
  }

  @media screen and (max-width: 480px) {
    top: 15%;
  }
`;

const LaptopImg = styled.div`
  width: 915px;
  height: 470px;
  position: absolute;
  top: -4%;
  left: -27.2%;
  right: 0;
  margin: 0 auto;
  z-index: 1;

  @media screen and (max-width: 906px) {
    left: -17%;
    right: 0;
    margin: 0 auto;
  }

  @media screen and (max-width: 710px) {
    top: 0px;
    left: 0px;
    right: 0;
    width: 100%;
    height: 100%;
  }
`;

const Laptop = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  height: 380px;
  width: 583px;

  @media screen and (max-width: 710px) {
    width: 452px !important;
    height: 296px !important;
  }

  @media screen and (max-width: 480px) {
    width: 372px !important;
    height: 240px !important;
  }
`;

const BookMediaImg = styled(ImageComp)<{ isImageUploaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  border-radius: ${(props) => (props.isImageUploaded ? '5px' : '0px')};
`;

export const V8: FC<HeroDetailedProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    backgroundColor = COLOR_ORANGE,
    textColor,
    benefits = [],
    meta,
    hidden
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { isCoverDisabled, pages, bookMedia, bookId } = useBook();
  const {
    pagesThumbnails,
    salesPageId,
    updateImageMeta,
    purchaseElementId,
    isWebsiteOnly,
    removeMetaTag
  } = useSales();
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [isLoading, setIsLoading] = useState(false);
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, pagesThumbnails, cover_thumbnail]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      setIsImageUploaded(true);
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const handleUpload = async (file: File) => {
    setIsLoading(true);
    if (!meta['accessorKey'] || !editable) return;
    const formData = new FormData();
    if (isWebsiteOnly) formData.append('name', 'book_mockup');
    else formData.append('type', meta['accessorKey']);
    removeMetaTag(meta['accessorKey'], 'image_placeholder');
    formData.append('file', file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        setResize({ width, height });
        updateImageMeta(meta['accessorKey'], width, 'width');
        updateImageMeta(meta['accessorKey'], height, 'height');
      };
      img.src = e.target?.result as string;
    };

    reader.readAsDataURL(file);
    if (isWebsiteOnly) {
      formData.append('file', file);
      await ApiService.post(`/book-generator/books/${bookId}/book_media/`, formData);
    } else {
      const response = await ApiService.post(`/sales/sales-page/${salesPageId}/media/`, formData);
      updateImageMeta(meta['accessorKey'], response.data.file, 'image');
    }
    setIsLoading(false);
  };

  const shadedBackgroundColor = useMemo(() => {
    return shade(backgroundColor, 0.85);
  }, [backgroundColor]);

  return (
    <HeroDetailed
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}>
      <WrapperTitle>
        <Title
          id={title.id}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
          }}
          style={{ color: filterBackgroundColor('#fff', textColor) }}
          onClick={updateActiveElement}
          onBlur={resetActiveElement}
          data-accessor-parent={meta['accessorKey']}
          data-accessorkey="title"
          data-placeholder="+"
          editable={process.env['APP_TYPE'] === 'dashboard'}
          isEmpty={isTitleEmpty}
        />
      </WrapperTitle>

      <StyledWrapper>
        <TitleWrapper>
          <Benefits>
            {benefits.map(({ content, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              return (
                <BenefitItem
                  key={index}
                  // style={{
                  //   display: isTitleBenEmpty && isContentBenEmpty && !editable ? 'flex' : 'none'
                  // }}
                  // TODO: Replace with dynamic values later
                >
                  <IconBenefit>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 15" fill="none">
                      <g clip-path="url(#clip0_5383_58089)">
                        <path
                          d="M7.75007 10.0699C7.84277 9.93837 7.88249 9.85942 7.94871 9.79363C10.7031 7.05675 13.4442 4.33303 16.1986 1.60931C16.9136 0.91193 17.8671 0.754033 18.6616 1.21457C19.7342 1.81984 19.9858 3.24091 19.2045 4.18829C19.1383 4.26724 19.0721 4.33303 18.9926 4.41198C15.7351 7.6357 12.4908 10.8594 9.23319 14.0963C8.62405 14.7016 7.90898 14.9121 7.08797 14.6358C6.80988 14.5437 6.5318 14.3595 6.31992 14.1489C4.66466 12.5305 3.03587 10.9121 1.39385 9.2673C0.811196 8.67519 0.625806 7.96465 0.917133 7.18833C1.20846 6.412 1.80436 5.97779 2.62537 5.89884C3.27423 5.84621 3.81716 6.09621 4.26739 6.55674C5.35325 7.64886 6.45234 8.72782 7.5382 9.80679C7.60441 9.87258 7.64414 9.93837 7.73683 10.0699H7.75007Z"
                          fill={backgroundColor}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_5383_58089">
                          <rect
                            width="18.87"
                            height="13.7897"
                            fill="white"
                            transform="translate(0.789062 0.953125)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </IconBenefit>
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="title"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={process.env['APP_TYPE'] === 'dashboard'}
                      isEmpty={isTitleBenEmpty}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="content"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={editable}
                      isEmpty={isContentBenEmpty}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </Benefits>
        </TitleWrapper>
        <RelativeWrapper>
          <LaptopImg>
            <AbsoluteInner>
              {bookMockup ? (
                <HoverableDropzone
                  isSalePage
                  css={imageStyles as never}
                  disabled={!editable}
                  resizeSelector={editable}
                  handleResize={({ width, height }) => {
                    setResize({ width, height });
                    updateImageMeta(meta['accessorKey'], width, 'width');
                    updateImageMeta(meta['accessorKey'], height, 'height');
                  }}
                  allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
                  onUpload={handleUpload}
                  loading={isLoading}
                  alreadyUploaded={Boolean(bookMockup)}
                  imageFile={bookMockup}
                  initialPosition={{
                    x: meta['x_1'] || 0,
                    y: meta['y_1'] || 0,
                    width: meta['d_width_1'],
                    height: meta['d_height_1']
                  }}
                  meta={meta}
                  positionEnabled
                  handlePosition={(width: number, height: number, x: number, y: number) => {
                    updateImageMeta(meta['accessorKey'], x, 'x_1');
                    updateImageMeta(meta['accessorKey'], y, 'y_1');
                    updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                    updateImageMeta(meta['accessorKey'], height, 'd_height_1');
                  }}
                  imageAspectRatio={{
                    ratio: '4x3',
                    pixels: '800x600'
                  }}>
                  <BookMediaImg
                    isImageUploaded={isImageUploaded}
                    src={bookMockup}
                    alt="Book Mockup"
                    width={resize.width || 500}
                    height={resize.height || 500}
                  />
                </HoverableDropzone>
              ) : (
                <StyledMockup
                  mockup={mockup?.file}
                  mockup_id={mockup?.id}
                  cover_thumbnail={coverThumbnail}
                />
              )}
            </AbsoluteInner>
            <ImageRelative>
              <Laptop
                src="https://pagewheel-public.s3.us-east-1.amazonaws.com/imgs/imacMockup2.png"
                alt="laptop"
              />
            </ImageRelative>
          </LaptopImg>
        </RelativeWrapper>
      </StyledWrapper>
      <BenefitWrapper>
        <Wrapper>
          <DescWrapper>
            <Description
              id={content.id}
              dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
              style={{ color: filterBackgroundColor('#fff', textColor) }}
              onClick={updateActiveElement}
              onBlur={resetActiveElement}
              data-accessor-parent={meta['accessorKey']}
              data-accessorkey="content"
              data-placeholder="+"
              editable={process.env['APP_TYPE'] === 'dashboard'}
              isEmpty={isContentEmpty}
            />
            {meta['url'] ? (
              <Download textColor={textColor} button={button} meta={meta} />
            ) : (
              <ButtonInner>
                <StyledButton to={purchaseElementId} button={button} meta={meta} />
              </ButtonInner>
            )}
          </DescWrapper>
        </Wrapper>
      </BenefitWrapper>
      <WhiteInner>
        <RelativeWhite>
          <div
            className="hero-white-v5 hero-white-v5_one"
            style={{ background: backgroundColor }}></div>
          <div
            className="hero-white-v5 hero-white-v5_two"
            style={{ background: backgroundColor }}></div>
          <div
            className="hero-white-v5 hero-white-v5_three"
            style={{ background: backgroundColor }}></div>
          <div
            className="hero-white-v5 hero-white-v5_four"
            style={{ background: backgroundColor }}></div>
          <div
            className="hero-white-v5 hero-white-v5_five"
            style={{ background: backgroundColor }}></div>
          <div
            className="hero-white-v5 hero-white-v5_six"
            style={{ background: backgroundColor }}></div>
        </RelativeWhite>
      </WhiteInner>
      <ColoredBg style={{ background: shadedBackgroundColor }} />
    </HeroDetailed>
  );
};
